"use client";

import { useTheme } from "next-themes";
import { useCallback, useEffect, useState } from "react";

interface ViewTransition {
    finished: Promise<void>;
    ready: Promise<void>;
    updateCallbackDone: Promise<void>;
    skipTransition(): void;
}

declare global {
    interface Document {
        startViewTransition(
            callback: () => void | Promise<void>
        ): ViewTransition;
    }
}

export type AnimationVariant =
    | "circle"
    | "rectangle"
    | "gif"
    | "polygon"
    | "circle-blur";
export type AnimationStart =
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right"
    | "center"
    | "top-center"
    | "bottom-center"
    | "bottom-up"
    | "top-down"
    | "left-right"
    | "right-left";

interface Animation {
    name: string;
    css: string;
}

interface ThemeToggleHookParams {
    variant?: AnimationVariant;
    start?: AnimationStart;
    blur?: boolean;
    gifUrl?: string;
    duration?: string;
}

/**
 * Converts animation start position to SVG circle coordinates
 */
const getPositionCoords = (position: AnimationStart) => {
    switch (position) {
        case "top-left":
            return { cx: "0", cy: "0" };
        case "top-right":
            return { cx: "40", cy: "0" };
        case "bottom-left":
            return { cx: "0", cy: "40" };
        case "bottom-right":
            return { cx: "40", cy: "40" };
        case "top-center":
            return { cx: "20", cy: "0" };
        case "bottom-center":
            return { cx: "20", cy: "40" };
        case "bottom-up":
        case "top-down":
        case "left-right":
        case "right-left":
            return { cx: "20", cy: "20" };
    }
};

const generateSVG = (variant: AnimationVariant, start: AnimationStart) => {
    if (variant === "circle-blur") {
        if (start === "center") {
            return `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><defs><filter id="blur"><feGaussianBlur stdDeviation="2"/></filter></defs><circle cx="20" cy="20" r="18" fill="white" filter="url(%23blur)"/></svg>`;
        }
        const positionCoords = getPositionCoords(start);
        if (!positionCoords) {
            throw new Error(`Invalid start position: ${start}`);
        }
        const { cx, cy } = positionCoords;
        return `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><defs><filter id="blur"><feGaussianBlur stdDeviation="2"/></filter></defs><circle cx="${cx}" cy="${cy}" r="18" fill="white" filter="url(%23blur)"/></svg>`;
    }

    if (start === "center") return;

    if (variant === "rectangle") return "";

    const positionCoords = getPositionCoords(start);
    if (!positionCoords) {
        throw new Error(`Invalid start position: ${start}`);
    }
    const { cx, cy } = positionCoords;

    if (variant === "circle") {
        return `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><circle cx="${cx}" cy="${cy}" r="20" fill="white"/></svg>`;
    }

    return "";
};

const getTransformOrigin = (start: AnimationStart) => {
    switch (start) {
        case "top-left":
            return "top left";
        case "top-right":
            return "top right";
        case "bottom-left":
            return "bottom left";
        case "bottom-right":
            return "bottom right";
        case "top-center":
            return "top center";
        case "bottom-center":
            return "bottom center";
        case "bottom-up":
        case "top-down":
        case "left-right":
        case "right-left":
            return "center";
    }
};

export const createAnimation = (
    variant: AnimationVariant,
    start: AnimationStart = "center",
    blur = false,
    url?: string,
    duration?: string
): Animation => {
    const svg = generateSVG(variant, start);
    const transformOrigin = getTransformOrigin(start);

    if (variant === "rectangle") {
        const getClipPath = (direction: AnimationStart) => {
            switch (direction) {
                case "bottom-up":
                    return {
                        from: "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)",
                        to: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)"
                    };
                case "top-down":
                    return {
                        from: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)",
                        to: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)"
                    };
                case "left-right":
                    return {
                        from: "polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)",
                        to: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)"
                    };
                case "right-left":
                    return {
                        from: "polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)",
                        to: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)"
                    };
                case "top-left":
                    return {
                        from: "polygon(0% 0%, 0% 0%, 0% 0%, 0% 0%)",
                        to: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)"
                    };
                case "top-right":
                    return {
                        from: "polygon(100% 0%, 100% 0%, 100% 0%, 100% 0%)",
                        to: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)"
                    };
                case "bottom-left":
                    return {
                        from: "polygon(0% 100%, 0% 100%, 0% 100%, 0% 100%)",
                        to: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)"
                    };
                case "bottom-right":
                    return {
                        from: "polygon(100% 100%, 100% 100%, 100% 100%, 100% 100%)",
                        to: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)"
                    };
                default:
                    return {
                        from: "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)",
                        to: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)"
                    };
            }
        };

        const clipPath = getClipPath(start);

        const defaultDuration =
            duration || (start === "top-down" ? "0.8s" : "1.2s");
        return {
            name: `${variant}-${start}${blur ? "-blur" : ""}`,
            css: `
       ::view-transition-group(root) {
        animation-duration: ${defaultDuration};
        animation-timing-function: ${
            start === "top-down"
                ? "cubic-bezier(0.4, 0, 1, 1)"
                : "cubic-bezier(0.16, 1, 0.3, 1)"
        };
      }

      ::view-transition-new(root) {
        animation-name: reveal-light-${start}${blur ? "-blur" : ""};
        ${blur ? "filter: blur(2px);" : ""}
      }

      ::view-transition-old(root),
      .dark::view-transition-old(root) {
        animation: none;
        z-index: -1;
      }
      .dark::view-transition-new(root) {
        animation-name: reveal-dark-${start}${blur ? "-blur" : ""};
        ${blur ? "filter: blur(2px);" : ""}
      }

      @keyframes reveal-dark-${start}${blur ? "-blur" : ""} {
        from {
          clip-path: ${clipPath.from};
          ${blur ? "filter: blur(8px);" : ""}
        }
        ${blur ? "50% { filter: blur(4px); }" : ""}
        to {
          clip-path: ${clipPath.to};
          ${blur ? "filter: blur(0px);" : ""}
        }
      }

      @keyframes reveal-light-${start}${blur ? "-blur" : ""} {
        from {
          clip-path: ${clipPath.from};
          ${blur ? "filter: blur(8px);" : ""}
        }
        ${blur ? "50% { filter: blur(4px); }" : ""}
        to {
          clip-path: ${clipPath.to};
          ${blur ? "filter: blur(0px);" : ""}
        }
      }
      `
        };
    }
    if (variant === "circle" && start == "center") {
        const defaultDuration = duration || "1.0s";
        return {
            name: `${variant}-${start}${blur ? "-blur" : ""}`,
            css: `
       ::view-transition-group(root) {
        animation-duration: ${defaultDuration};
        animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
      }

      ::view-transition-new(root) {
        animation-name: reveal-light${blur ? "-blur" : ""};
        ${blur ? "filter: blur(2px);" : ""}
      }

      ::view-transition-old(root),
      .dark::view-transition-old(root) {
        animation: none;
        z-index: -1;
      }
      .dark::view-transition-new(root) {
        animation-name: reveal-dark${blur ? "-blur" : ""};
        ${blur ? "filter: blur(2px);" : ""}
      }

      @keyframes reveal-dark${blur ? "-blur" : ""} {
        from {
          clip-path: circle(0% at 50% 50%);
          ${blur ? "filter: blur(8px);" : ""}
        }
        ${blur ? "50% { filter: blur(4px); }" : ""}
        to {
          clip-path: circle(100.0% at 50% 50%);
          ${blur ? "filter: blur(0px);" : ""}
        }
      }

      @keyframes reveal-light${blur ? "-blur" : ""} {
        from {
           clip-path: circle(0% at 50% 50%);
           ${blur ? "filter: blur(8px);" : ""}
        }
        ${blur ? "50% { filter: blur(4px); }" : ""}
        to {
          clip-path: circle(100.0% at 50% 50%);
          ${blur ? "filter: blur(0px);" : ""}
        }
      }
      `
        };
    }
    if (variant === "gif") {
        const defaultDuration = duration || "3s";
        return {
            name: `${variant}-${start}`,
            css: `
      ::view-transition-group(root) {
  animation-timing-function: var(--expo-in);
}

::view-transition-new(root) {
  mask: url('${url}') center / 0 no-repeat;
  animation: scale ${defaultDuration};
}

::view-transition-old(root),
.dark::view-transition-old(root) {
  animation: scale ${defaultDuration};
}

@keyframes scale {
  0% {
    mask-size: 0;
  }
  10% {
    mask-size: 50vmax;
  }
  90% {
    mask-size: 50vmax;
  }
  100% {
    mask-size: 2000vmax;
  }
}`
        };
    }

    if (variant === "circle-blur") {
        const defaultDuration = duration || "1s";
        if (start === "center") {
            return {
                name: `${variant}-${start}`,
                css: `
        ::view-transition-group(root) {
          animation-timing-function: var(--expo-out);
        }

        ::view-transition-new(root) {
          mask: url('${svg}') center / 0 no-repeat;
          mask-origin: content-box;
          animation: scale ${defaultDuration};
          transform-origin: center;
        }

        ::view-transition-old(root),
        .dark::view-transition-old(root) {
          animation: scale ${defaultDuration};
          transform-origin: center;
          z-index: -1;
        }

        @keyframes scale {
          to {
            mask-size: 350vmax;
          }
        }
        `
            };
        }

        return {
            name: `${variant}-${start}`,
            css: `
      ::view-transition-group(root) {
        animation-timing-function: var(--expo-out);
      }

      ::view-transition-new(root) {
        mask: url('${svg}') ${start.replace("-", " ")} / 0 no-repeat;
        mask-origin: content-box;
        animation: scale ${defaultDuration};
        transform-origin: ${transformOrigin};
      }

      ::view-transition-old(root),
      .dark::view-transition-old(root) {
        animation: scale ${defaultDuration};
        transform-origin: ${transformOrigin};
        z-index: -1;
      }

      @keyframes scale {
        to {
          mask-size: 350vmax;
        }
      }
      `
        };
    }

    if (variant === "polygon") {
        const getPolygonClipPaths = (position: AnimationStart) => {
            switch (position) {
                case "top-left":
                    return {
                        darkFrom:
                            "polygon(50% -71%, -50% 71%, -50% 71%, 50% -71%)",
                        darkTo: "polygon(50% -71%, -50% 71%, 50% 171%, 171% 50%)",
                        lightFrom:
                            "polygon(171% 50%, 50% 171%, 50% 171%, 171% 50%)",
                        lightTo:
                            "polygon(171% 50%, 50% 171%, -50% 71%, 50% -71%)"
                    };
                case "top-right":
                    return {
                        darkFrom:
                            "polygon(150% -71%, 250% 71%, 250% 71%, 150% -71%)",
                        darkTo: "polygon(150% -71%, 250% 71%, 50% 171%, -71% 50%)",
                        lightFrom:
                            "polygon(-71% 50%, 50% 171%, 50% 171%, -71% 50%)",
                        lightTo:
                            "polygon(-71% 50%, 50% 171%, 250% 71%, 150% -71%)"
                    };
                default:
                    return {
                        darkFrom:
                            "polygon(50% -71%, -50% 71%, -50% 71%, 50% -71%)",
                        darkTo: "polygon(50% -71%, -50% 71%, 50% 171%, 171% 50%)",
                        lightFrom:
                            "polygon(171% 50%, 50% 171%, 50% 171%, 171% 50%)",
                        lightTo:
                            "polygon(171% 50%, 50% 171%, -50% 71%, 50% -71%)"
                    };
            }
        };

        const clipPaths = getPolygonClipPaths(start);
        const defaultDuration = duration || "1.0s";

        return {
            name: `${variant}-${start}${blur ? "-blur" : ""}`,
            css: `
      ::view-transition-group(root) {
        animation-duration: ${defaultDuration};
        animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
      }

      ::view-transition-new(root) {
        animation-name: reveal-light-${start}${blur ? "-blur" : ""};
        ${blur ? "filter: blur(2px);" : ""}
      }

      ::view-transition-old(root),
      .dark::view-transition-old(root) {
        animation: none;
        z-index: -1;
      }
      .dark::view-transition-new(root) {
        animation-name: reveal-dark-${start}${blur ? "-blur" : ""};
        ${blur ? "filter: blur(2px);" : ""}
      }

      @keyframes reveal-dark-${start}${blur ? "-blur" : ""} {
        from {
          clip-path: ${clipPaths.darkFrom};
          ${blur ? "filter: blur(8px);" : ""}
        }
        ${blur ? "50% { filter: blur(4px); }" : ""}
        to {
          clip-path: ${clipPaths.darkTo};
          ${blur ? "filter: blur(0px);" : ""}
        }
      }

      @keyframes reveal-light-${start}${blur ? "-blur" : ""} {
        from {
          clip-path: ${clipPaths.lightFrom};
          ${blur ? "filter: blur(8px);" : ""}
        }
        ${blur ? "50% { filter: blur(4px); }" : ""}
        to {
          clip-path: ${clipPaths.lightTo};
          ${blur ? "filter: blur(0px);" : ""}
        }
      }
      `
        };
    }

    if (variant === "circle" && start !== "center") {
        const getClipPathPosition = (position: AnimationStart) => {
            switch (position) {
                case "top-left":
                    return "0% 0%";
                case "top-right":
                    return "100% 0%";
                case "bottom-left":
                    return "0% 100%";
                case "bottom-right":
                    return "100% 100%";
                case "top-center":
                    return "50% 0%";
                case "bottom-center":
                    return "50% 100%";
                default:
                    return "50% 50%";
            }
        };

        const clipPosition = getClipPathPosition(start);
        const defaultDuration = duration || "1.2s";

        return {
            name: `${variant}-${start}${blur ? "-blur" : ""}`,
            css: `
       ::view-transition-group(root) {
        animation-duration: ${defaultDuration};
        animation-timing-function: var(--expo-out);
      }

      ::view-transition-new(root) {
        animation-name: reveal-light-${start}${blur ? "-blur" : ""};
        ${blur ? "filter: blur(2px);" : ""}
      }

      ::view-transition-old(root),
      .dark::view-transition-old(root) {
        animation: none;
        z-index: -1;
      }
      .dark::view-transition-new(root) {
        animation-name: reveal-dark-${start}${blur ? "-blur" : ""};
        ${blur ? "filter: blur(2px);" : ""}
      }

      @keyframes reveal-dark-${start}${blur ? "-blur" : ""} {
        from {
          clip-path: circle(0% at ${clipPosition});
          ${blur ? "filter: blur(8px);" : ""}
        }
        ${blur ? "50% { filter: blur(4px); }" : ""}
        to {
          clip-path: circle(150.0% at ${clipPosition});
          ${blur ? "filter: blur(0px);" : ""}
        }
      }

      @keyframes reveal-light-${start}${blur ? "-blur" : ""} {
        from {
           clip-path: circle(0% at ${clipPosition});
           ${blur ? "filter: blur(8px);" : ""}
        }
        ${blur ? "50% { filter: blur(4px); }" : ""}
        to {
          clip-path: circle(150.0% at ${clipPosition});
          ${blur ? "filter: blur(0px);" : ""}
        }
      }
      `
        };
    }

    const defaultDuration = duration || "1s";
    return {
        name: `${variant}-${start}${blur ? "-blur" : ""}`,
        css: `
      ::view-transition-group(root) {
        animation-timing-function: var(--expo-in);
      }
      ::view-transition-new(root) {
        mask: url('${svg}') ${start.replace("-", " ")} / 0 no-repeat;
        mask-origin: content-box;
        animation: scale-${start}${blur ? "-blur" : ""} ${defaultDuration};
        transform-origin: ${transformOrigin};
        ${blur ? "filter: blur(2px);" : ""}
      }
      ::view-transition-old(root),
      .dark::view-transition-old(root) {
        animation: scale-${start}${blur ? "-blur" : ""} ${defaultDuration};
        transform-origin: ${transformOrigin};
        z-index: -1;
      }
      @keyframes scale-${start}${blur ? "-blur" : ""} {
        from {
          ${blur ? "filter: blur(8px);" : ""}
        }
        ${blur ? "50% { filter: blur(4px); }" : ""}
        to {
          mask-size: 2000vmax;
          ${blur ? "filter: blur(0px);" : ""}
        }
      }
    `
    };
};

export const useAnimatedThemeToggle = ({
    variant = "circle",
    start = "center",
    blur = false,
    gifUrl = "",
    duration
}: ThemeToggleHookParams = {}) => {
    const { theme, setTheme, resolvedTheme } = useTheme();
    const [isDark, setIsDark] = useState(false);

    useEffect(() => {
        setIsDark(resolvedTheme === "dark");
    }, [resolvedTheme]);

    const styleId = "theme-transition-styles";

    const updateStyles = useCallback((css: string, name: string) => {
        if (typeof window === "undefined") return;

        let styleElement = document.getElementById(styleId) as HTMLStyleElement;

        if (!styleElement) {
            styleElement = document.createElement("style");
            styleElement.id = styleId;
            document.head.appendChild(styleElement);
        }

        styleElement.textContent = css;
    }, []);

    const toggleTheme = useCallback(() => {
        setIsDark(!isDark);

        const animation = createAnimation(
            variant,
            start,
            blur,
            gifUrl,
            duration
        );
        updateStyles(animation.css, animation.name);

        if (typeof window === "undefined") return;

        const switchTheme = () => {
            setTheme(theme === "light" ? "dark" : "light");
        };

        if (!document.startViewTransition) {
            switchTheme();
            return;
        }

        document.startViewTransition(switchTheme);
    }, [
        theme,
        setTheme,
        variant,
        start,
        blur,
        gifUrl,
        duration,
        updateStyles,
        isDark,
        setIsDark
    ]);

    const setCrazyLightTheme = useCallback(() => {
        setIsDark(false);

        const animation = createAnimation(
            variant,
            start,
            blur,
            gifUrl,
            duration
        );
        updateStyles(animation.css, animation.name);

        if (typeof window === "undefined") return;

        const switchTheme = () => {
            setTheme("light");
        };

        if (!document.startViewTransition) {
            switchTheme();
            return;
        }

        document.startViewTransition(switchTheme);
    }, [
        setTheme,
        variant,
        start,
        blur,
        gifUrl,
        duration,
        updateStyles,
        setIsDark
    ]);

    const setCrazyDarkTheme = useCallback(() => {
        setIsDark(true);

        const animation = createAnimation(
            variant,
            start,
            blur,
            gifUrl,
            duration
        );
        updateStyles(animation.css, animation.name);

        if (typeof window === "undefined") return;

        const switchTheme = () => {
            setTheme("dark");
        };

        if (!document.startViewTransition) {
            switchTheme();
            return;
        }

        document.startViewTransition(switchTheme);
    }, [
        setTheme,
        variant,
        start,
        blur,
        gifUrl,
        duration,
        updateStyles,
        setIsDark
    ]);

    return {
        isDark,
        setIsDark,
        toggleTheme,
        setCrazyLightTheme,
        setCrazyDarkTheme
    };
};
